import { PROCESS_SAMPLE_DURATION_SECONDS } from '@electron/shared';
import { observer } from 'mobx-react-lite';
import { type ComponentProps, useEffect, useState } from 'react';

import { Button } from '@/components/ui';
import { electronApis } from '@/features/electron-api';
import { userStore } from '@/stores/user.store';
import { DISCORD_URL } from '@/utils/constants';
import { handleExternalUrlClick, handleMailTo } from '@/utils/platform';

import { SettingsSection } from '../../settings-section';

import { SyncDiagnostics } from './sync-diagnostics';
import { useProcessSampleConfirmationModal } from './use-process-sample';

export const SupportSettings = () => {
  const [isProcessSampleAvailable, setIsProcessSampleAvailable] =
    useState(false);

  useEffect(() => {
    electronApis.system
      .isProcessSampleAvailable()
      .then(setIsProcessSampleAvailable);
  }, []);

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Support</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <EmailSupport />
        <SettingsSection.Seperator />
        <DiscordSupport />
        <SettingsSection.Seperator />
        <SyncDiagnostics />
        {isProcessSampleAvailable && (
          <>
            <SettingsSection.Seperator />
            <ProcessSampleCapture />
          </>
        )}
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
};

/**
 * For unfiform button with consistent width
 */
const SupportButton = (props: ComponentProps<typeof Button>) => (
  <Button variant="secondary" className="min-w-32" {...props} />
);

const EmailSupport = observer(() => {
  const user = userStore.user;

  const email = 'support@littlebird.ai';
  const subject = 'I need assistance';
  const body = `Please support my account with: ${user?.email}`;

  return (
    <SettingsSection.Item searchId="support.email-support">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Email Support</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Contact us at support@littlebird.ai
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SupportButton onClick={() => handleMailTo(email, subject, body)}>
        Email Support
      </SupportButton>
    </SettingsSection.Item>
  );
});

const DiscordSupport = () => {
  return (
    <SettingsSection.Item searchId="support.discord">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Join our Discord</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Join our Discord server for real time support and discussions
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SupportButton onClick={() => handleExternalUrlClick(DISCORD_URL)}>
        Join Discord
      </SupportButton>
    </SettingsSection.Item>
  );
};

/**
 * Let the user capture a process sample to help us troubleshoot performance issues
 */
const ProcessSampleCapture = () => {
  const { isCapturing, onCaptureSampleClick } =
    useProcessSampleConfirmationModal();

  return (
    <SettingsSection.Item searchId="support.report-performance">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Report Performance Issues
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Experiencing slowdowns or high resource usage? Create a{' '}
          {PROCESS_SAMPLE_DURATION_SECONDS}-second diagnostic snapshot to help
          us troubleshoot the issue.
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SupportButton
        onClick={onCaptureSampleClick}
        disabled={isCapturing}
        isLoading={isCapturing}
      >
        Report
      </SupportButton>
    </SettingsSection.Item>
  );
};
